import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { debounce } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { Row } from 'reactstrap';

import ContentContainer from '../../components/ContentContainer';
import PageTitle from '../../components/PageTitle';
import withUser from '../../hoc/withUser';
import CardUserInfo from './components/CardUserInfo';
import Search from '../../components/Search';
import './components/MenuSeparator/styles.scss';
import DropdownThreeDots from '../../components/DropdownThreeDots';
import Spinner from '../../components/Spinner';
import ConfirmationModal from '../../components/Modal';
import { getTitleRemoveUserProfileModal } from './utils/string-transformation';
import { DEBOUNCE_TIMEOUT } from '../../utils/constants';
import { useStore } from '../../store';

const CompanyAdminUsersPage = observer(({ user, authToken }) => {
  const {
    rootStore: {
      profileStore,
      profileStore: { companyStore },
    },
  } = useStore();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [modalAttributes, setModalAttributes] = useState({
    placeholder: '',
    title: '',
    legalObligation: null,
  });

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      companyStore.setAdminUsersPageLoading(true);
      if (!profileStore.profileData) {
        await profileStore.initProfile(user, authToken, i18n);
      }
      companyStore.resetCompanyStoreFields();
      await profileStore.loadUser();
      await companyStore.getAllUsers().then(() => companyStore.setAdminUsersPageLoading(false));
    };

    fetchData();
    // eslint-disable-next-line
  }, []);

  const getUserCardDropdownOptions = (isAdmin, assigneeId, userFullName, companyName) => {
    if (isAdmin) {
      return [
        {
          label: t('companyAdminUsers.removeCompanyAdmin'),
          method: () => {
            setModalAttributes({
              placeholder: t('companyAdminUsers.removeCompanyAdmin'),
              title: getTitleRemoveUserProfileModal(
                t('userCard.confirmationModal.titleRemoveCompanyAdmin'),
                userFullName
              ),
              method: () => companyStore.removeCompanyAdmin(assigneeId, userFullName, companyName, t),
            });
            setShowConfirmationModal(true);
          },
        },
      ];
    }
    return [
      {
        label: t('companyAdminUsers.assignCompanyAdmin'),
        method: () => {
          setModalAttributes({
            placeholder: t('companyAdminUsers.assignCompanyAdmin'),
            title: getTitleRemoveUserProfileModal(
              t('userCard.confirmationModal.titleAssignAsCompanyAdmin'),
              userFullName
            ),
            method: () => companyStore.assignAsCompanyAdmin(assigneeId, userFullName, companyName, t),
          });
          setShowConfirmationModal(true);
        },
      },
      {
        label: t('companyAdminUsers.removeUser'),
        method: () => {
          setModalAttributes({
            placeholder: t('userCard.confirmationModal.placeholder'),
            legalObligation: t('userCard.confirmationModal.legalObligation'),
            title: getTitleRemoveUserProfileModal(t('userCard.confirmationModal.title'), userFullName),
            method: () => companyStore.removeUserProfile(assigneeId, userFullName, companyName, t),
          });
          setShowConfirmationModal(true);
        },
      },
    ];
  };

  const getAllUsersBySearchTerm = searchTerm => {
    setSearchLoading(true);
    companyStore.getAllUsersBySearchTerm(searchTerm).then(() => setSearchLoading(false));
  };

  const onSearchChange = debounce(term => {
    getAllUsersBySearchTerm(term);
  }, DEBOUNCE_TIMEOUT);

  return (
    <ContentContainer fullWidth={true}>
      <PageTitle titleKey={t('companyAdminUsers.title')} subtitleKey={t('companyAdminUsers.subtitle')} />
      <Search placeholder="Search" onChange={e => onSearchChange(e.target.value)} isLoading={searchLoading} />
      {searchLoading ? (
        <Spinner />
      ) : (
        <InfiniteScroll
          dataLength={companyStore.userInfoList.length}
          next={() => companyStore.getAllUsers()}
          hasMore={companyStore.userInfoList.length < companyStore.totalUsersInDatabase}
          loader={<Spinner />}
          style={{ overflow: 'hidden' }}
        >
          <Row className="styled-row-shared">
            {companyStore.userInfoList?.map(userRow => (
              <CardUserInfo
                key={userRow.id}
                fullName={userRow.fullName}
                firstName={userRow.firstName}
                lastName={userRow.lastName}
                address={userRow.address}
                email={userRow.email}
                userFunction={userRow.userFunction}
                isCompanyAdmin={userRow.isAdmin}
                pictureSrc={userRow.pictureSrc}
                branchAddress={userRow.branchAddress}
                clientIconUrl={userRow.clientIconUrl}
                clientIconName={userRow.clientIconName}
              >
                {profileStore.profileData.email === userRow.email ? null : (
                  <DropdownThreeDots
                    options={getUserCardDropdownOptions(
                      userRow.isAdmin,
                      userRow.id,
                      userRow.fullName,
                      userRow.companyName
                    )}
                  />
                )}
              </CardUserInfo>
            ))}
          </Row>
        </InfiniteScroll>
      )}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        placeholder={modalAttributes.placeholder}
        title={modalAttributes.title}
        legal={modalAttributes.legalObligation}
        confirmButtonTitle={t('deleteConfirmModal.deleteButton')}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={async () => {
          setShowConfirmationModal(false);
          await modalAttributes.method();
        }}
      />
    </ContentContainer>
  );
});

export default withUser(CompanyAdminUsersPage);
